import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type UseQueryResult } from '@/lib/query';
import { Users, Search } from 'lucide-react';
import { useOrganizationUsers } from '@/modules/users/hooks/useOrganizationUsers';
import { DriverGroupDto, OrganizationUserDto, CompanyAccessKeyDto } from '@cargovibe/shared-types';
import { useCompanyAccessKey } from '@/modules/users/hooks/useCompanyAccessKey';
import { Input } from '@/shared/ui/input';
import { DataTableShell } from '@/shared/ui/data-table-shell';
import { QueryErrorMessage } from '@/shared/ui/QueryErrorMessage';
import { ActivationKeyBadge } from '@/modules/users/components/ActivationKeyBadge';
import { UsersPageHeader } from '@/modules/users/components/UsersPageHeader';
import { EditUserDialog } from '@/modules/users/components/EditUserDialog';
import { DeleteUserDialog } from '@/modules/users/components/DeleteUserDialog';
import {
  UsersTable,
  type SortDirection,
  type UserSortKey,
} from '@/modules/users/components/UsersTable';
import { filterShownUserTableEntries } from '@/modules/users/filters/filterShownUserTableEntries';
import {
  CreateDriverGroupDialog,
  DeleteDriverGroupDialog,
  DriverGroupEditBar,
  DriverGroupPills,
  toGroupsByUserId,
  useDriverGroupWorkflow,
  useDriverGroups,
} from '@/modules/driver-groups';
import { Toast } from '@/shared/ui/Toast';

export default function UsersPage(): React.JSX.Element {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [editingUser, setEditingUser] = useState<OrganizationUserDto | null>(null);
  const [deletingUser, setDeletingUser] = useState<OrganizationUserDto | null>(null);
  const [sortKey, setSortKey] = useState<UserSortKey>('lastname');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const {
    data: users,
    isLoading,
    error,
  }: UseQueryResult<OrganizationUserDto[]> = useOrganizationUsers();
  const { data: groups }: UseQueryResult<DriverGroupDto[]> = useDriverGroups();

  const driverGroups: DriverGroupDto[] = useMemo<DriverGroupDto[]>(() => groups ?? [], [groups]);
  const workflow: ReturnType<typeof useDriverGroupWorkflow> = useDriverGroupWorkflow(driverGroups);
  const { editor }: ReturnType<typeof useDriverGroupWorkflow> = workflow;
  const activeGroup: DriverGroupDto | null = editor.activeGroup;

  const { data: accessKey, error: accessKeyError }: UseQueryResult<CompanyAccessKeyDto | null> =
    useCompanyAccessKey();

  const groupsByUserId: Map<string, DriverGroupDto[]> = useMemo<Map<string, DriverGroupDto[]>>(
    () => toGroupsByUserId(driverGroups),
    [driverGroups]
  );

  const visibleUsers: OrganizationUserDto[] = useMemo<OrganizationUserDto[]>(
    () =>
      filterShownUserTableEntries({
        users,
        searchTerm,
        sortKey,
        sortDirection,
        activeGroup,
        isSelectingMembers: editor.isEditing,
      }),
    [users, searchTerm, sortKey, sortDirection, activeGroup, editor.isEditing]
  );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchTerm(event.target.value);
  };

  const handleSort = (key: UserSortKey): void => {
    if (sortKey === key) {
      setSortDirection((currentDirection) => (currentDirection === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDirection('asc');
    }
  };

  const handleCloseEdit = (): void => {
    setEditingUser(null);
  };

  const handleCloseDelete = (): void => {
    setDeletingUser(null);
  };

  const resolveAccessKeyContent = (): React.ReactNode => {
    if (accessKey) return <ActivationKeyBadge activationKey={accessKey} />;
    if (accessKeyError)
      return (
        <p className="text-sm text-destructive" aria-live="polite">
          {t('common.error')}
        </p>
      );
    return null;
  };

  if (error) return <QueryErrorMessage error={error} />;

  const accessKeyContent: React.ReactNode = resolveAccessKeyContent();

  return (
    <div className="space-y-6">
      <UsersPageHeader title={t('users.title')} subtitle={t('users.subtitle')} />

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        {accessKeyContent}
        <div className="relative w-full sm:ml-auto sm:max-w-xl">
          <Search
            size={20}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground/60"
          />
          <Input
            placeholder={t('common.search')}
            value={searchTerm}
            onChange={handleSearchChange}
            className="h-12 pl-12 text-base"
          />
        </div>
      </div>

      <DriverGroupPills
        groups={driverGroups}
        activeGroupId={editor.activeGroupId}
        allDriversCount={users?.length ?? 0}
        onSelect={editor.selectGroup}
        onCreate={workflow.openCreateGroup}
        onEdit={workflow.startEditingActiveGroup}
        onDelete={workflow.requestDeleteGroup}
      />

      {editor.isEditing && (
        <DriverGroupEditBar
          name={editor.draftName}
          onNameChange={editor.setDraftName}
          errorMessage={workflow.groupNameError}
          isDirty={editor.isDirty}
          isNameValid={editor.isNameValid}
          isSaving={editor.isSaving}
          onCancel={editor.cancelEditing}
          onSave={workflow.saveGroup}
        />
      )}

      <DataTableShell
        icon={Users}
        count={visibleUsers.length}
        countLabel={t('users.userLabel')}
        isLoading={isLoading}
        isEmpty={visibleUsers.length === 0}
      >
        <UsersTable
          users={visibleUsers}
          groupsByUserId={groupsByUserId}
          sortKey={sortKey}
          sortDirection={sortDirection}
          onSort={handleSort}
          isSelectingMembers={editor.isEditing}
          selectedMemberIds={editor.draftMemberIds}
          onToggleMember={editor.toggleMember}
          onEditUser={setEditingUser}
          onDeleteUser={setDeletingUser}
        />
      </DataTableShell>

      <EditUserDialog key={editingUser?.id} user={editingUser} onClose={handleCloseEdit} />
      <DeleteUserDialog user={deletingUser} onClose={handleCloseDelete} />
      <CreateDriverGroupDialog
        isOpen={workflow.isCreateGroupOpen}
        onClose={workflow.closeCreateGroup}
        onCreated={workflow.handleGroupCreated}
      />
      <DeleteDriverGroupDialog
        group={workflow.groupPendingDeletion}
        onClose={workflow.closeDeleteGroup}
        onDeleted={workflow.handleGroupDeleted}
      />
      <Toast message={workflow.toastMessage} onDismiss={workflow.dismissToast} />
    </div>
  );
}
